import { useTranslate } from '@tolgee/react';
import { Collapse } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FileDrop } from 'react-file-drop';
import { useIntercom } from 'react-use-intercom';
import styled from 'styled-components';

import CuripodAIStarsSvg from '../../../../assets/icons/CuripodAIStarsSvg';
import FoldedHandsSVG from '../../../../assets/icons/FoldedHands';
import theme from '../../../../assets/theme';
import { LicenseCoarseLevels, UserAccessBadge } from '../../../../hooks/useAccessBadge';
import useAuthGuardPrompt from '../../../../hooks/useAuthGuardPrompt';
import { useMetrics } from '../../../../hooks/useMetrics';
import { PaywallMetadata } from '../../../../hooks/usePaywallPrompt';
import { useUploadPresentation } from '../../../../hooks/useUploadPresentation';
import { PromptInput } from '../../../../services/backendService/types';
import { GeneratorCoarseLicenseLevel } from '../../../../services/TemplateService/Template.type';
import {
  MAX_LIMIT_LEARNING_OBJECTIVE,
  MAX_LIMIT_PROMPT,
} from '../../../../utils/aiGeneratorUtils';
import { getFiles } from '../../../../utils/fileDropUtils';
import { debounceEventIfNotTrusted } from '../../../../utils/general';
import { isIos } from '../../../../utils/navigator';
import { Input } from '../../../_atoms/_Input';
import { Loader } from '../../../_atoms/_Loader';
import { TextAreaV2 } from '../../../_atoms/_TextArea/TextAreaV2';
import { Button } from '../../../_atoms/Button';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import BaseModal from '../../../_molecules/BaseModal';
import {
  GeneratorFormState,
  UploadPassageInput,
} from '../../../_organisms/DesignerTabPane/PromptsTab/GeneratorForm';
import { TestPrepFloatingSelect } from '../components/FloatingSelect';
import { FormErrorText, OrangeButton } from './TestPrepTab';

export function TestPrepGeneratorForm({
  promptInputs,
  handleInputChange,
  handleMultipleInputsChange,
  handleSubmit,
  isLoading,
  isError,
  error,
  formState,
  promptInputSuggestions,
  isPremiumGenerator,
  generatorPlan,
  generatorId,
  preFillSearch,
  preFillGrade,
  promptPaywall,
  isReady,
  userAccessBadge,
}: {
  promptInputs: PromptInput[];
  promptInputSuggestions?: PromptInput[][];
  handleInputChange: (
    inputId: string,
    value: string | number | { filePath: string; fileType?: string },
  ) => void;
  handleMultipleInputsChange?: (
    inputsToChange: {
      inputId: string;
      value: string | number | { filePath: string; fileType: string };
    }[],
  ) => void;
  handleSubmit: () => void;
  isLoading?: boolean;
  isError?: boolean;
  error?: any;
  formState?: GeneratorFormState;
  isPremiumGenerator?: boolean | null;
  generatorPlan?: GeneratorCoarseLicenseLevel | null;
  generatorId?: string;
  preFillSearch?: string;
  preFillGrade?: string;
  promptPaywall: (metadata?: PaywallMetadata | undefined) => void;
  isReady: boolean;
  userAccessBadge: UserAccessBadge | undefined;
}) {
  const [showErrorModal, setShowErrorModal] = useState(false);
  const metrics = useMetrics();
  const { showNewMessages } = useIntercom();
  const { authGuardComponent, showAuthGuard } = useAuthGuardPrompt(
    undefined,
    undefined,
    generatorId,
  );
  const requiredInputs = useMemo(() => promptInputs.filter(p => p.isRequired), [
    promptInputs,
  ]);

  const optionalInputs = useMemo(() => promptInputs.filter(p => !p.isRequired), [
    promptInputs,
  ]);
  const currentPromptInputSuggestions = usePromptInputSuggestions({
    inputSuggestions: promptInputSuggestions,
    formState,
    promptInputs,
  });

  const [isFormErrorVisible, setIsFormErrorVisible] = useState(false);

  useEffect(() => {
    if (isError) {
      metrics.logEvent('TestPrep.GeneratorError', {
        generatorId: generatorId,
        error: error?.message,
      });
      setShowErrorModal(true);
    }
  }, [error, generatorId, isError, metrics]);

  //Pre fill form with search and or grade
  useEffect(() => {
    if (preFillSearch && !preFillGrade) {
      const topicInput = promptInputs.find(p => p.label.includes('topic'));

      if (topicInput) {
        handleInputChange(topicInput.id, preFillSearch);
      }

      return;
    }
    if (!preFillSearch && preFillGrade) {
      const gradeInput = promptInputs.find(p => p.id.includes('grade'));
      if (gradeInput) {
        handleInputChange(gradeInput.id, preFillGrade);
      }

      return;
    }

    if (preFillSearch && preFillGrade) {
      const topicInput = promptInputs.find(p => p.label.includes('topic'));
      const gradeInput = promptInputs.find(p => p.id.includes('grade'));

      if (topicInput && gradeInput && handleMultipleInputsChange) {
        handleMultipleInputsChange([
          { inputId: topicInput.id, value: preFillSearch },
          { inputId: gradeInput.id, value: preFillGrade },
        ]);
      }
      return;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preFillSearch, preFillGrade]);

  const isFormEnabled = useMemo(() => {
    if (formState) {
      let errorMessage = '';
      const isEnabled = requiredInputs.every(input => {
        const realValue = formState?.[input.id];
        if (!realValue || realValue === '') {
          errorMessage = `Don't forget to fill out: ${input.label}`;
        }
        return realValue && realValue !== '';
      });
      return { isEnabled, errorMessage };
    }
    return { isEnabled: false, errorMessage: 'Make sure you fill out all the boxes' };
  }, [requiredInputs, formState]);

  return (
    <>
      {authGuardComponent}
      <BaseModal visible={showErrorModal} onCancel={() => setShowErrorModal(false)}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <IconWrapper height="120px" width="120px" style={{ marginTop: 20 }}>
            <FoldedHandsSVG />
          </IconWrapper>

          <ErrorMessage style={{ fontWeight: 800 }}>
            Uh-oh! Something went wrong on our end
          </ErrorMessage>

          <ErrorMessage style={{ fontSize: '16px' }}>
            {error?.customMessage ||
              'Please try again or contact support if the issue persists'}
          </ErrorMessage>

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <Button width="120px" onClick={() => setShowErrorModal(false)}>
              Ok
            </Button>
            <Button
              width="120px"
              onClick={() => {
                const errorMessage = error?.customMessage || 'Unknown error occurred';
                showNewMessages(
                  `I'm having issues with lesson generation and need help.\n\nError: ${errorMessage}`,
                );
                setShowErrorModal(false);
              }}
            >
              Get Help
            </Button>
          </div>
        </div>
      </BaseModal>
      <GridContainer>
        {requiredInputs?.map(promptInput => (
          <TestPrepInputElement
            key={promptInput.id}
            promptInput={promptInput}
            promptInputSuggestions={currentPromptInputSuggestions}
            value={formState?.[promptInput.id]}
            onChange={val => handleInputChange(promptInput.id, val)}
            promptPaywall={promptPaywall}
            userAccessBadge={userAccessBadge}
            isReady={isReady}
          />
        ))}

        {optionalInputs.length > 0 && (
          <OptionalInputCollapse ghost style={{ width: '100%' }}>
            <OptionalInputCollapsePanel
              header={'Extra details (optional)'}
              key="optional"
              style={{ color: theme.colors.black, fontWeight: 'bold' }}
            >
              <OptionalGridContainer>
                {optionalInputs?.map(promptInput => (
                  <TestPrepInputElement
                    key={promptInput.id}
                    value={formState?.[promptInput.id]}
                    promptInputSuggestions={currentPromptInputSuggestions}
                    promptInput={promptInput}
                    onChange={val => handleInputChange(promptInput.id, val)}
                    promptPaywall={promptPaywall}
                    userAccessBadge={userAccessBadge}
                    isReady={isReady}
                  />
                ))}
              </OptionalGridContainer>
            </OptionalInputCollapsePanel>
          </OptionalInputCollapse>
        )}
      </GridContainer>
      {isFormErrorVisible && !isFormEnabled.isEnabled && (
        <FormErrorText>{isFormEnabled.errorMessage}</FormErrorText>
      )}
      {isReady &&
      generatorPlan === GeneratorCoarseLicenseLevel.enterprise &&
      userAccessBadge?.license.coarseLevel !== LicenseCoarseLevels.enterprise ? (
        <OrangeButton
          type="primary"
          loading={isLoading}
          $disabled={!isFormEnabled.isEnabled}
          onClick={e => {
            debounceEventIfNotTrusted({
              cb: () => {
                if (!isFormEnabled.isEnabled) {
                  setIsFormErrorVisible(true);
                  return;
                }
                if (
                  generatorPlan === GeneratorCoarseLicenseLevel.enterprise &&
                  userAccessBadge?.license.coarseLevel !== LicenseCoarseLevels.enterprise
                ) {
                  promptPaywall({
                    label: 'Upgrade account test prep',
                    description: `Generator form: ${generatorId}`,
                  });
                  return;
                } else {
                  handleSubmit();
                }
              },
              isTrusted: e.isTrusted,
            });
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 10,
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <IconWrapper height="30px" width="30px">
              <CuripodAIStarsSvg />
            </IconWrapper>

            <span>Generate Lesson</span>
          </div>
        </OrangeButton>
      ) : (
        <OrangeButton
          type="primary"
          loading={isLoading}
          $disabled={!isFormEnabled.isEnabled}
          onClick={e => {
            debounceEventIfNotTrusted({
              cb: () => {
                if (!isFormEnabled.isEnabled) {
                  setIsFormErrorVisible(true);
                  return;
                }

                if (!isReady) {
                  showAuthGuard();
                  return;
                }
                if (
                  isPremiumGenerator &&
                  userAccessBadge?.license.coarseLevel !== LicenseCoarseLevels.premium &&
                  userAccessBadge?.license.coarseLevel !== LicenseCoarseLevels.enterprise
                ) {
                  promptPaywall({
                    label: 'Upgrade account test prep',
                    description: `Generator form: ${generatorId}`,
                  });
                  return;
                } else {
                  handleSubmit();
                }
              },
              isTrusted: e.isTrusted,
            });
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 10,
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <IconWrapper height="30px" width="30px">
              <CuripodAIStarsSvg />
            </IconWrapper>

            <span>Generate Lesson</span>
          </div>
        </OrangeButton>
      )}
    </>
  );
}

function TestPrepInputElement({
  promptInput,
  onChange,
  promptInputSuggestions,
  value,
  promptPaywall,
  userAccessBadge,
  isReady,
}: {
  promptInput: PromptInput;
  promptInputSuggestions?: PromptInput[];
  onChange: (val: number | string | { filePath: string; fileType?: string }) => void;
  value?: string | number | { filePath: string; fileType?: string };
  promptPaywall: (metadata?: PaywallMetadata | undefined) => void;
  userAccessBadge: UserAccessBadge | undefined;
  isReady: boolean;
}) {
  const { t } = useTranslate();
  const [passageValue, setPassageValue] = useState<
    { filePath: string; fileType?: string } | undefined
  >(value as { filePath: string; fileType: string } | undefined);

  const handleFileUploaded = (uploadedFile: { filePath: string; fileType?: string }) => {
    onChange({ filePath: uploadedFile.filePath, fileType: uploadedFile.fileType });
  };
  const renderedSuggestion = useSinglePromptInputSuggestion({
    promptInput,
    promptInputSuggestions: promptInputSuggestions,
    value,
  });

  if (promptInput.type === 'shortText') {
    return (
      <div>
        <Input
          style={{ width: '100%' }}
          type="text"
          borderLess
          inputStyle={{
            boxShadow: theme.effects.floatingCreatorElementShadow,
            border: 'none',
          }}
          value={value as string | undefined}
          label={promptInput.label}
          placeholder={
            (renderedSuggestion as string) ||
            t('Add your {{promptInputLabel}}', 'Add your {{promptInputLabel}}', {
              promptInputLabel: promptInput.label,
            })
          }
          labelStyle={{
            fontSize: '16px',
            color: theme.colors.black,
            fontWeight: 'bold',
          }}
          maxLength={promptInput.constraints?.maxLength || MAX_LIMIT_PROMPT}
          error={
            value &&
            (value as string).length >=
              (promptInput.constraints?.maxLength || MAX_LIMIT_PROMPT)
              ? `${(value as string).length}/${promptInput.constraints?.maxLength ||
                  MAX_LIMIT_PROMPT}`
              : undefined
          }
          onChange={e => onChange(e.target.value)}
        />
      </div>
    );
  }
  if (promptInput.type === 'longText') {
    return (
      <TextAreaV2
        label={promptInput.label}
        labelStyle={{
          fontSize: '16px',
          fontWeight: 'bold',
          color: theme.colors.black,
        }}
        rows={3}
        placeholder={renderedSuggestion || 'Add your ' + promptInput.label}
        symbolCount={true}
        maxLength={promptInput.constraints?.maxLength || MAX_LIMIT_LEARNING_OBJECTIVE}
        value={value as string | undefined}
        error={
          value &&
          (value as string).length >=
            (promptInput.constraints?.maxLength || MAX_LIMIT_LEARNING_OBJECTIVE)
            ? `${(value as string).length}/${promptInput.constraints?.maxLength ||
                MAX_LIMIT_LEARNING_OBJECTIVE}`
            : undefined
        }
        textAreaStyled={{
          minHeight: '100px',
          boxShadow: theme.effects.floatingCreatorElementShadow,
          border: 'none',
        }}
        onChange={e => onChange(e.target.value)}
      />
    );
  }
  if (promptInput.type === 'dropdown') {
    return (
      <TestPrepFloatingSelect
        label={promptInput.label}
        value={value as string | undefined}
        options={promptInput.constraints?.options || []}
        onSelect={e => onChange(e as string | number)}
        promptPaywall={promptPaywall}
        userAccessBadge={userAccessBadge}
        isReady={isReady}
      />
    );
  }

  if (promptInput.type === 'passage') {
    return (
      <UploadPassageInput
        onFileUploaded={handleFileUploaded}
        uploadedFile={value as { filePath: string; fileType: string } | undefined}
        onChange={onChange}
        label={promptInput.label}
        promptInput={promptInput}
        passageValue={passageValue}
        setPassageValue={setPassageValue}
        labelStyle={{
          fontSize: '16px',
          fontWeight: 'bold',
          color: theme.colors.black,
        }}
        style={{
          boxShadow: theme.effects.floatingCreatorElementShadow,
          border: 'none',
          background: theme.colors.white,
          minHeight: '100px',
          paddingBottom: '40px',
        }}
      />
    );
  }
  if (promptInput.type == 'slidesUpload') {
    return (
      <TestPrepInputFileElement
        onChange={onChange}
        label={promptInput.label}
        value={value as { filePath: string; fileType: string } | undefined}
      />
    );
  }

  return null;
}

function usePromptInputSuggestions({
  inputSuggestions,
  formState,
  promptInputs,
}: {
  inputSuggestions?: PromptInput[][];
  formState: GeneratorFormState | undefined;
  promptInputs: PromptInput[];
}) {
  /**
   * used for finding the current input suggestion to render
   * */

  const intervalRef = useRef<NodeJS.Timeout>();
  const [selectedInputSuggestionIndex, setSelectedInputSuggestionIndex] = useState(0);
  // makes a interval that changes the placeholder every 4 seconds
  useEffect(() => {
    if (!inputSuggestions) return;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // if we have filled in all the text inputs, we don't need to render the placeholder
    if (formState) {
      const textPromptInputs = promptInputs.filter(
        e => e.type === 'shortText' || e.type === 'longText',
      );
      if (textPromptInputs.every(p => formState[p.id])) {
        return;
      }
    }

    intervalRef.current = setInterval(() => {
      setSelectedInputSuggestionIndex(i => (i + 1) % inputSuggestions.length);
    }, 3000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [selectedInputSuggestionIndex, inputSuggestions, formState, promptInputs]);

  const currentInputSuggestions = useMemo(() => {
    if (!inputSuggestions) return [];
    return inputSuggestions[selectedInputSuggestionIndex];
  }, [inputSuggestions, selectedInputSuggestionIndex]);

  return currentInputSuggestions;
}

function useSinglePromptInputSuggestion({
  promptInput,
  promptInputSuggestions = [],
  value,
}: {
  promptInput: PromptInput;
  promptInputSuggestions?: PromptInput[];
  value?: string | number | { filePath: string; fileType?: string };
}) {
  const [suggestionToRender, setSuggestionToRender] = useState<string | undefined>(
    undefined,
  );
  const intervalRef = useRef<NodeJS.Timeout>();
  const [renderedSuggestion, setRenderedSuggestion] = useState<string>('');
  const intervalTime = useMemo(() => {
    if (suggestionToRender && suggestionToRender?.length > 10) {
      return Math.max(1000 / suggestionToRender.length, 10);
    }
    return 100;
  }, [suggestionToRender]);

  // when the prompt input changes, we need to change the suggestion
  useEffect(() => {
    // if the prompt input is not short or long text, we don't need to render the placeholder
    if (promptInput.type !== 'shortText' && promptInput.type !== 'longText') return;

    // if the prompt input has a value, we don't need to render the placeholder
    if (value) return;

    const newSuggestion = promptInputSuggestions?.find(p => p.id === promptInput.id);
    if (newSuggestion) {
      setRenderedSuggestion('');
      setSuggestionToRender(newSuggestion.value);
      return;
    }

    // if there is no suggestion, we remove the placeholder
    if (promptInputSuggestions && promptInputSuggestions.length > 0) {
      setRenderedSuggestion('');
      setSuggestionToRender(undefined);
      return;
    }
  }, [promptInput, promptInputSuggestions, value]);

  // when the suggestion changes, we need to create the dynamic writing experience and thus render the placeholder
  useEffect(() => {
    if (intervalRef?.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => {
      // this logic makes the rendered placeholder string look likes its being typed
      if (suggestionToRender) {
        setRenderedSuggestion(e => {
          return suggestionToRender?.slice(0, e.length + 1);
        });
      }
    }, intervalTime);

    return () => {
      if (intervalRef?.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [intervalTime, renderedSuggestion, suggestionToRender]);
  return renderedSuggestion;
}

const GridContainer = styled.div`
  display: grid;
  width: 100%;
  gap: 30px;
  grid-template-columns: 1fr;
`;

const OptionalInputCollapsePanel = styled(Collapse.Panel)`
  background-color: transparent;
  margin-top: 10px;
`;

const OptionalInputCollapse = styled(Collapse)`
  background-color: transparent;
  .ant-collapse-header {
    padding: 0 !important;
    font-size: 16px;
  }
  .ant-collapse-content-box {
    padding: 0;
  }
`;

const OptionalGridContainer = styled.div`
  display: grid;
  width: 100%;
  gap: 14px;
  grid-template-columns: 1fr;
`;

const ErrorMessage = styled.h4`
  font-weight: bold;
  width: 100%;
  text-align: center;
  margin-top: 20px;
  font-size: 18px;

  p {
    margin-top: 10px;
    font-size: 14px;
  }
`;

function TestPrepInputFileElement({
  onChange,
  value,
  label,
}: {
  value: { filePath: string; fileType: string } | undefined;
  onChange: (val: number | string | { filePath: string; fileType: string }) => void;
  label: string;
}) {
  const {
    fileInfo,
    upload: uploadPresentation,
    isUploading: isUploadingFile,
    isSuccess: uploadPresentationSuccess,
    isError: isErrorUploadingFile,
    reset: resetFileUpload,
  } = useUploadPresentation();
  const [fileSizeExceeded, setFileSizeExceeded] = useState(false);

  const [uploadedFile, setUploadedFile] = useState<
    | {
        filePath: string;
        fileType: string;
      }
    | undefined
  >(value);
  const handleFileSelected = useCallback(
    (file: File) => {
      const fileSizeInMb = file.size * 1e-6;
      if (fileSizeInMb <= 100) {
        setFileSizeExceeded(false);
        uploadPresentation({ file });
      }

      if (fileSizeInMb > 100) {
        setFileSizeExceeded(true);
      }
    },
    [uploadPresentation],
  );

  useEffect(() => {
    if (uploadPresentationSuccess && fileInfo) {
      setUploadedFile(fileInfo);
      resetFileUpload();
      onChange(fileInfo);
    }
  }, [fileInfo, onChange, uploadPresentationSuccess, resetFileUpload]);
  return (
    <TestPrepFileUploadArea
      handleFileSelected={handleFileSelected}
      isUploadingFile={isUploadingFile}
      uploadedFile={uploadedFile}
      setUploadedFile={setUploadedFile}
      isErrorUploadingFile={isErrorUploadingFile}
      resetFileUpload={resetFileUpload}
      fileSizeExceeded={fileSizeExceeded}
      label={label}
    />
  );
}

function TestPrepFileUploadArea({
  uploadedFile,
  setUploadedFile,
  isUploadingFile,
  isErrorUploadingFile,
  handleFileSelected,
  resetFileUpload,
  fileSizeExceeded,
  label = 'Upload Slides',
}: {
  uploadedFile: { filePath: string; fileType: string } | undefined;
  setUploadedFile: (
    uploadedFile:
      | {
          filePath: string;
          fileType: string;
        }
      | undefined,
  ) => void;
  isUploadingFile: boolean;
  isErrorUploadingFile: boolean;
  handleFileSelected: (file: File) => void;
  resetFileUpload: () => void;
  fileSizeExceeded: boolean;
  label?: string;
}) {
  const [fileName, setFileName] = useState<string | undefined>();

  useEffect(() => {
    if (uploadedFile) {
      //Cut off the first section of the file path to give the name of the file
      const index = uploadedFile.filePath?.indexOf('_');

      if (index !== -1) {
        const findFileName = uploadedFile.filePath.substring(index + 1);
        setFileName(findFileName);
      }
    }
  }, [uploadedFile]);

  return (
    <>
      {!(isUploadingFile || uploadedFile || isErrorUploadingFile) && (
        <div
          style={{
            width: '100%',
          }}
        >
          <p
            style={{
              fontSize: 16,
              marginBottom: 4,
              color: theme.colors.black,
              fontWeight: 'bold',
            }}
          >
            {label}
          </p>

          {fileSizeExceeded && (
            <ErrorMessage style={{ fontSize: 12, textAlign: 'left' }}>
              Oops looks like this presentation is too big. Please try uploading a file
              smaller than 100MB
            </ErrorMessage>
          )}
          <TestPrepPresentationFileSelector onFileSelected={handleFileSelected} />
        </div>
      )}
      {(isUploadingFile || uploadedFile || isErrorUploadingFile) && (
        <div
          style={{
            alignItems: 'space-beetween',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {isUploadingFile && (
            <div
              style={{
                gridColumn: '1/3',
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Loader />
              <h3
                style={{ width: '100%', textAlign: 'center', color: theme.colors.black }}
              >
                Uploading
              </h3>
            </div>
          )}
          {isErrorUploadingFile && (
            <div
              style={{
                width: '100%',
              }}
            >
              <IconWrapper height="100px" width="100px">
                <FoldedHandsSVG />
              </IconWrapper>
              <h3 style={{ width: '100%', textAlign: 'center' }}>Failed to upload</h3>
              <p style={{ textAlign: 'center' }}>
                Make sure the file is correct format, not corrupted and there is no
                password protection
              </p>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  type="primary"
                  onClick={() => {
                    resetFileUpload();
                    setUploadedFile(undefined);
                  }}
                >
                  Please try again
                </Button>
              </div>
            </div>
          )}
          {uploadedFile && (
            <div
              style={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 5,
                alignItems: 'center',
              }}
            >
              <h3
                style={{
                  margin: 0,
                  width: '100%',
                  wordBreak: 'break-all',
                  color: theme.colors.black,
                }}
              >
                {fileName}{' '}
                <span style={{ fontSize: 10, display: 'block' }}>
                  successfully uploaded
                </span>
              </h3>
              <Button type="ghost" onClick={() => setUploadedFile(undefined)}>
                Change
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
const validFileTypes = [
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', //pptx
  'key',
  'application/pdf',
];

function TestPrepPresentationFileSelector(props: {
  onFileSelected: (file: File) => void;
  metricsName?: string;
}) {
  const { onFileSelected, metricsName } = props;

  const metrics = useMetrics();
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files) {
      onFileSelected(files[0]);
    }
  };

  const handleActivate = () => {
    metrics.logEvent(metricsName || 'selectFile');

    if (ref.current) {
      ref.current.value = '';
      ref.current!.click();
    }
  };

  const ref = useRef<HTMLInputElement>(null);

  const handleFilesDropped = (
    files: FileList | null,
    event: React.DragEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    if (files) {
      const parsedFiles = getFiles(files);
      const invalidFiles = parsedFiles.filter(
        file => !validFileTypes.includes(file.type) && !file.name.endsWith('.key'),
      );
      if (invalidFiles.length > 0) {
        alert('Invalid file type, please try again');
        return;
      }

      if (parsedFiles.length > 0) {
        metrics.logEvent('TestPrep.ImportPresentation.DropFile', {
          count: parsedFiles.length,
          type: parsedFiles[0].type,
          size: parsedFiles[0].size,
          name: parsedFiles[0].name,
        });
        onFileSelected(parsedFiles[0]);
      }
    }
  };
  return (
    <>
      <input
        data-testid="FileInput"
        ref={ref}
        type="file"
        name="myFile"
        multiple={false}
        accept={isIos ? '*' : '.pptx, .key, .ppt, .pdf'}
        onChange={onChange}
        style={{ display: 'none' }}
      />
      <ChildWrapper
        data-testid="FileInputClickButton"
        tabIndex={0}
        onKeyPress={e => {
          if (e.key === 'Enter' || e.key === 'space') {
            handleActivate();
          }
        }}
        onClick={handleActivate}
      >
        <TestPrepPresentationFileDrop onFilesDropped={handleFilesDropped} />
      </ChildWrapper>
    </>
  );
}

function TestPrepPresentationFileDrop({
  onFilesDropped,
}: {
  onFilesDropped?: (
    files: FileList | null,
    event: React.DragEvent<HTMLDivElement>,
  ) => void;
}) {
  return (
    <Container>
      {/* @ts-ignore */}
      <FileDrop onDrop={(files, event) => onFilesDropped && onFilesDropped(files, event)}>
        <ContentContainer>
          <Title>Drag and drop or click to select a presentation</Title>
          <Description>Supported file types are .pptx, .ppt, .key, .pdf</Description>
        </ContentContainer>
      </FileDrop>
    </Container>
  );
}

const Title = styled.p`
  &&& {
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 0px;
    margin-top: 0px;
    text-align: center;
    user-select: none;
    color: ${theme.colors.black};
  }

  @media (max-width: 500px) {
    &&& {
      font-size: 14px;
    }
  }
`;

const Description = styled.p`
  &&& {
    font-size: 14px;
    font-weight: normal;
    margin-top: 0px;
    margin-bottom: 0px;
    text-align: center;
    user-select: none;
    color: ${theme.colors.black};
  }
  @media (max-width: 500px) {
    &&& {
      font-size: 12px;
    }
  }
`;

const ContentContainer = styled.div`
  height: 100%;
  width: 100%;
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding: 20px;
  border-radius: 10px;
  border: 2px dashed ${theme.colors.lightBlue};
  background-color: ${theme.colors.lessTranslucentWhite};
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  cursor: pointer;

  &:hover {
    background-color: ${theme.colors.lightBlue};
    border: 2px dashed ${theme.colors.white};
    transition: all 0.4s;
  }

  &:active {
    background-color: ${theme.colors.lightBlue};
    border: 2px dashed ${theme.colors.white};
    transition: all 0.4s;
  }
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;

  .file-drop {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    flex: 1;
  }
  .file-drop-target {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  .file-drop > .file-drop-target.file-drop-dragging-over-frame > div {
    background-color: ${theme.colors.lightBlue};
    border: 2px dashed ${theme.colors.white};
    transition: all 0.4s;
  }
`;

const ChildWrapper = styled.div`
  width: inherit;
  height: inherit;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  &:focus-within {
    outline: none;
  }
`;
